import React, { useState } from 'react';
import { Heart, Users, Clock, Send, CheckCircle, HandHeart, Sparkles, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Volunteer = () => {
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    skills: '',
    availability: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) return;
    setSubmitting(true);
    setError('');
    try {
      await axios.post(`${BACKEND_URL}/api/public/volunteer`, form);
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  return (
    <div>
      {/* Header */}
      <div className="bg-[#BA1826] py-8 text-center">
        <p className="text-xs uppercase tracking-[0.2em] text-white/70 font-semibold mb-2">Join Our Team</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold text-white">Volunteer With Us</h1>
      </div>

      {/* Why Volunteer */}
      <section className="py-10 px-6 bg-white">
        <div className="max-w-lg mx-auto text-center mb-8">
          <p className="text-sm text-[#4B5563] leading-relaxed">
            Whether you can give an hour or a day, your time and skills help families receive the 
            support they need — delivered with dignity and care.
          </p>
        </div>

        <div className="grid grid-cols-3 gap-4 max-w-lg mx-auto mb-8">
          {[
            { icon: HandHeart, title: 'Direct Impact', desc: 'Hands-on work that changes lives' },
            { icon: Users, title: 'Community', desc: 'Join a team that cares deeply' },
            { icon: Sparkles, title: 'Growth', desc: 'Build skills while giving back' },
          ].map((item) => (
            <div key={item.title} className="text-center p-4 bg-[#F8F9FA] rounded-lg" data-testid={`volunteer-benefit-${item.title.toLowerCase()}`}>
              <item.icon className="w-6 h-6 text-[#BA1826] mx-auto mb-2" />
              <h3 className="font-heading text-sm font-semibold mb-1">{item.title}</h3>
              <p className="text-xs text-[#4B5563]">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Form Section */}
      <section className="py-10 px-6 bg-[#F8F9FA]">
        <div className="max-w-lg mx-auto">
          {submitted ? (
            <div className="bg-white border border-[#E5E7EB] rounded-lg p-8 text-center" data-testid="volunteer-success">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="font-heading text-2xl font-bold text-[#0A0A0A] mb-3">Thank You!</h2>
              <p className="text-sm text-[#4B5563] mb-6">
                Your volunteer interest has been submitted. Our team will review your information 
                and reach out to discuss next steps.
              </p>
              <div className="flex flex-wrap justify-center gap-3">
                <button
                  onClick={() => { setSubmitted(false); setForm({ name: '', email: '', phone: '', skills: '', availability: '', message: '' }); }}
                  className="text-sm text-[#BA1826] hover:underline"
                  data-testid="submit-another-btn"
                >
                  Submit another application
                </button>
                <Link to="/" className="btn-primary text-sm" data-testid="return-home-btn">
                  Return Home <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          ) : (
            <div className="bg-white border border-[#E5E7EB] rounded-lg p-6">
              <h2 className="font-heading text-xl font-bold text-[#0A0A0A] mb-1">Volunteer Interest Form</h2>
              <p className="text-xs text-[#4B5563] mb-5">Fill out the form below and we'll be in touch.</p>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 text-sm p-3 rounded mb-4" data-testid="volunteer-error">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4" data-testid="volunteer-form">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-[#4B5563] mb-1">Full Name *</label>
                    <input
                      type="text"
                      required
                      value={form.name}
                      onChange={(e) => update('name', e.target.value)}
                      placeholder="Your full name"
                      className="w-full px-3 py-2 border border-[#E5E7EB] rounded-sm text-sm focus:border-[#BA1826] focus:outline-none"
                      data-testid="volunteer-name-input"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[#4B5563] mb-1">Email *</label>
                    <input
                      type="email"
                      required
                      value={form.email}
                      onChange={(e) => update('email', e.target.value)}
                      placeholder="you@example.com"
                      className="w-full px-3 py-2 border border-[#E5E7EB] rounded-sm text-sm focus:border-[#BA1826] focus:outline-none"
                      data-testid="volunteer-email-input"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-[#4B5563] mb-1">Phone (optional)</label>
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={(e) => update('phone', e.target.value)}
                    placeholder="Your phone number"
                    className="w-full px-3 py-2 border border-[#E5E7EB] rounded-sm text-sm focus:border-[#BA1826] focus:outline-none"
                    data-testid="volunteer-phone-input"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-[#4B5563] mb-1">Skills & Experience</label>
                  <input
                    type="text"
                    value={form.skills}
                    onChange={(e) => update('skills', e.target.value)}
                    placeholder="e.g. Cooking, Driving, Translation, Event Planning"
                    className="w-full px-3 py-2 border border-[#E5E7EB] rounded-sm text-sm focus:border-[#BA1826] focus:outline-none"
                    data-testid="volunteer-skills-input"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-[#4B5563] mb-1">Availability</label>
                  <select
                    value={form.availability}
                    onChange={(e) => update('availability', e.target.value)}
                    className="w-full px-3 py-2 border border-[#E5E7EB] rounded-sm text-sm text-[#4B5563] focus:border-[#BA1826] focus:outline-none"
                    data-testid="volunteer-availability-select"
                  >
                    <option value="">Select your availability</option>
                    <option value="Weekdays">Weekdays</option>
                    <option value="Weekends">Weekends</option>
                    <option value="Evenings">Evenings</option>
                    <option value="Flexible">Flexible</option>
                    <option value="One-time event">One-time event</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-[#4B5563] mb-1">Why do you want to volunteer?</label>
                  <textarea
                    value={form.message}
                    onChange={(e) => update('message', e.target.value)}
                    placeholder="Tell us about yourself and why you'd like to get involved..."
                    rows={4}
                    maxLength={1000}
                    className="w-full px-3 py-2 border border-[#E5E7EB] rounded-sm text-sm focus:border-[#BA1826] focus:outline-none resize-none"
                    data-testid="volunteer-message-input"
                  />
                </div>

                <button
                  type="submit"
                  disabled={submitting || !form.name.trim() || !form.email.trim()}
                  className="btn-primary w-full py-2.5 disabled:opacity-50"
                  data-testid="volunteer-submit-btn"
                >
                  {submitting ? 'Submitting...' : 'Submit Application'}
                  {!submitting && <Send className="w-4 h-4" />}
                </button>

                <p className="text-xs text-[#4B5563] text-center">
                  We respect your privacy. Your information will only be used for volunteer coordination.
                </p>
              </form>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-10 bg-white text-center px-6">
        <p className="text-sm text-[#4B5563] mb-4">
          Can't volunteer right now? You can still make a difference.
        </p>
        <Link to="/donate" className="btn-primary text-sm" data-testid="volunteer-donate-cta">
          Make a Donation <ArrowRight className="w-4 h-4" />
        </Link>
      </section>
    </div>
  );
};

export default Volunteer;
